import { EditLineData, ProductVariants } from '@zonofi/seller-rtk-store';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FlexAlignType,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-easy-icon';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from '../helpers/responsive-native';
import { SHARED_SVGS } from '../assets';
import { colors } from '../colors/colors';
import { ThemeContext } from '../contexts/ThemeContext';
import ProductQuantityInfo from '../product-quantity-info/product-quantity-info';
import { CustomSvg } from '../custom-svg/custom-svg';

interface AddToOrderProps {
  product: ProductVariants;
  productInOrder: EditLineData;
  setOrderData?: (
    actionType: string,
    productVariantId: number,
    updatedLineData: EditLineData,
    poFileLineId?: string,
    addMultipleLines?: EditLineData[],
    fromAddProduct?: boolean
  ) => void;
  showDelete?: boolean;
  alignment?: FlexAlignType | undefined;
  fromPoDetails?: boolean;
  editable?: boolean;
  showInfo?: boolean;
  fromAddProduct?: boolean;
  addingProductToOrder?: boolean;
}

export const AddToOrder: React.FC<AddToOrderProps> = ({
  product,
  productInOrder,
  setOrderData,
  showDelete = true,
  alignment,
  fromPoDetails = false,
  editable = false,
  showInfo = false,
  fromAddProduct = false,
  addingProductToOrder,
}) => {
  const currentTheme = useContext(ThemeContext);
  const { theme } = currentTheme;

  const [value, setValue] = useState(productInOrder?.newQty?.toString());
  const [moqError, setMoqError] = useState(false);
  const [justMounted, setJustMounted] = useState(true);

  const productMOQ = useMemo(
    () => product?.minOrderQty || product?.promotions?.[0]?.minQtyASC || 1,
    [product]
  );

  const onAdd = () => {
    let disc = 0;
    if (product?.promotions) {
      disc = product?.promotions[0]?.discount
        ? Number(product?.promotions[0]?.discount)
        : 0;
    }

    const newLineData: EditLineData = {
      skuCode: product?.sku,
      productTitle: {
        text: product?.name,
        promotionId:
          !_.isUndefined(product?.promotions) &&
          !_.isUndefined(product?.promotions[0])
            ? Number(product?.promotions[0])
            : 0,
      },
      pts: product?.price - disc,
      unitPrice: product?.price - disc,
      basePrice: product?.basePrice,
      unitPriceWithTax: product?.PTR ?? 0,
      uploadedQty: 0,
      orderedAmt: 0,
      id: '',
      productVariantId: product?.productVariantId,
      newQty: product?.minOrderQty ?? product?.promotions?.[0]?.minQtyASC,
      isEdited: true,
      multiMapping: '-',
      divisionId: product?.division && product?.division[0]?.divisionId,
      divisionName: (product?.division && product?.division[0]?.name) || '',
      minOrderQty: productMOQ,
    };
    setOrderData &&
      setOrderData(
        'addLine',
        productInOrder?.productVariantId,
        newLineData,
        productInOrder?.poFileLineId
      );
  };

  const onIncrement = () => {
    const newLineData: EditLineData = { ...productInOrder };
    if (productInOrder?.newQty) {
      newLineData.newQty = productInOrder?.newQty + productMOQ;
      newLineData.isEdited = true;
      // newLineData.orderedAmt = productInOrder?.pts * newLineData?.newQty;
    }
    setOrderData &&
      setOrderData(
        'updateLine',
        productInOrder?.productVariantId,
        newLineData,
        productInOrder?.poFileLineId,
        [],
        fromAddProduct
      );
  };

  const onDecrement = () => {
    const newLineData: EditLineData = { ...productInOrder, isEdited: true };

    if (productInOrder?.newQty && productInOrder?.newQty <= productMOQ) {
      setMoqError(true);
      setTimeout(() => {
        setMoqError(false);
      }, 2500);
    } else {
      if (
        productInOrder?.newQty &&
        productInOrder?.newQty !== 0 &&
        !(productInOrder?.newQty - productMOQ < productMOQ)
      ) {
        newLineData.newQty = productInOrder?.newQty - productMOQ;
        // newLineData.orderedAmt = productInOrder?.pts * newLineData?.newQty;
      }
      if (newLineData.newQty === 0) {
        setOrderData &&
          setOrderData(
            'deleteLine',
            productInOrder?.productVariantId,
            newLineData,
            productInOrder?.poFileLineId,
            [],
            fromAddProduct
          );
      } else {
        setOrderData &&
          setOrderData(
            'updateLine',
            productInOrder?.productVariantId,
            newLineData,
            productInOrder?.poFileLineId,
            [],
            fromAddProduct
          );
      }
    }
  };

  const handleBlur = () => {
    if (!justMounted) {
      onEditQty();
    } else {
      setJustMounted(false);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setJustMounted(false);
    }, 1000);
  }, []);

  const onDelete = () => {
    setOrderData &&
      setOrderData(
        'deleteLine',
        productInOrder?.productVariantId,
        productInOrder,
        productInOrder?.poFileLineId,
        [],
        fromAddProduct
      );
  };

  const onEditQty = () => {
    const newLineData: EditLineData = { ...productInOrder };
    if (productInOrder?.newQty && value) {
      // newQty should be multiplier of productMOQ;
      newLineData.newQty = Math.ceil(Number(value) / productMOQ) * productMOQ;
      newLineData.isEdited = true;
    }
    if (newLineData?.newQty === 0) {
      setOrderData &&
        setOrderData(
          'deleteLine',
          productInOrder?.productVariantId,
          productInOrder,
          productInOrder?.poFileLineId
        );
    } else {
      if (Number(value) < productMOQ) {
        setMoqError(true);
        setTimeout(() => {
          setMoqError(false);
        }, 2500);
        setValue(
          (Math.ceil(Number(value) / productMOQ) * productMOQ)?.toString()
        );
      } else {
        setOrderData &&
          setOrderData(
            'updateLine',
            productInOrder?.productVariantId,
            newLineData,
            productInOrder?.poFileLineId,
            [],
            fromAddProduct
          );
      }
    }
  };

  useEffect(() => {
    setValue(productInOrder?.newQty?.toString());
  }, [productInOrder?.newQty]);

  return (
    <View style={{ alignItems: alignment }}>
      {productInOrder?.newQty ? (
        editable ? (
          <View style={styles.cartContainer}>
            {product?.baseUom && (
              <Text style={[theme.fontConfig.regular, styles.uomText]}>
                {product?.baseUom}
              </Text>
            )}
            <View style={{ flexDirection: 'column' }}>
              <View
                style={[
                  styles.counterContainer,
                  {
                    borderColor: '#32A7E8',
                    backgroundColor: '#EBF6FD',
                  },
                ]}
              >
                <TouchableOpacity
                  style={{
                    flex: 1,
                  }}
                  onPress={() => onDecrement()}
                >
                  {/* <Icon
                    type="antdesign"
                    name="minus"
                    color={theme?.table.underline}
                    size={20}
                    style={[styles.incrementButtons]}
                  /> */}
                  <View style={styles.incrementButtons}>
                    <SHARED_SVGS.MinusBlueNewIcon height={16} width={16} />
                  </View>
                </TouchableOpacity>

                <TextInput
                  value={value}
                  onChangeText={(val) => {
                    if (isFinite(Number(val)) && Number(val) >= 0) {
                      setValue(val);
                    }
                  }}
                  onBlur={handleBlur}
                  style={[
                    styles.inputBox,
                    {
                      // backgroundColor: theme?.addToCart?.input,
                      backgroundColor: '#EBF6FD',
                      fontSize: 14,
                    },
                  ]}
                />
                <TouchableOpacity
                  style={{ flex: 1 }}
                  onPress={() => onIncrement()}
                >
                  {/* <Icon
                    type="antdesign"
                    name="plus"
                    size={20}
                    color={theme?.table.underline}
                    style={[styles.incrementButtons]}
                  /> */}
                  <View style={styles.incrementButtons}>
                    <SHARED_SVGS.AddBlueNewIcon height={16} width={16} />
                  </View>
                </TouchableOpacity>
              </View>

              {moqError && (
                <View
                  // @ts-ignore
                  style={{
                    backgroundColor: theme.status.danger,
                    borderRadius: 4,
                    paddingHorizontal: '0.5vw',
                    paddingVertical: '0.2vh',
                    alignItems: 'center',
                    position: 'absolute',
                    top: '80%',
                  }}
                >
                  <Text
                    style={{
                      ...theme.fontConfig.semiBold,
                      fontSize: 10,
                      color: colors.white.color,
                      textAlign: 'center',
                    }}
                  >
                    Qty can’t be below product MOQ
                  </Text>
                </View>
              )}
            </View>
            {showInfo && (
              <View style={{ marginLeft: 4, marginBottom: 2 }}>
                <ProductQuantityInfo
                  orderedQty={Number(productInOrder.newQty) ?? 0}
                  uploadedQty={productInOrder.uploadedQty ?? 0}
                  minOrderQty={productInOrder.minOrderQty ?? 0}
                />
              </View>
            )}

            {showDelete ? (
              <TouchableOpacity
                onPress={() => onDelete()}
                style={{ marginLeft: 5, marginRight: 5, alignItems: 'center' }}
              >
                <SHARED_SVGS.DeleteIconNewComp height={20} width={20} />
              </TouchableOpacity>
            ) : null}
          </View>
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text
              style={{
                fontSize: 16,
                fontFamily: theme.fontConfig.regular.fontFamily,
                color: theme.text.primary,
                marginRight: 2,
              }}
            >
              {productInOrder.newQty}
            </Text>
            {showInfo && (
              <ProductQuantityInfo
                orderedQty={Number(productInOrder.newQty) ?? 0}
                uploadedQty={productInOrder.uploadedQty ?? 0}
                minOrderQty={productInOrder.minOrderQty ?? 0}
              />
            )}
          </View>
        )
      ) : fromPoDetails ? null : (
        <Pressable
          onPress={addingProductToOrder ? null : onAdd}
          disabled={false}
          style={[
            {
              padding: 10,
              flexDirection: 'row',
              alignItems: 'center',
              opacity: addingProductToOrder ? 0.5 : 1,
            },
          ]}
        >
          <SHARED_SVGS.AddBlueNewIcon height={14} width={14} />
          <Text
            style={[
              theme.fontConfig.medium,
              {
                fontSize: 14,
                color: theme?.table.underline,
                marginLeft: 4,
                // opacity: addingProductToOrder ? 0.5 : 1,
              },
            ]}
          >
            Add
          </Text>
        </Pressable>
      )}
    </View>
  );
};
const styles = StyleSheet.create({
  cartContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
  },
  counterContainer: {
    flexDirection: 'row',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    minWidth: 120,
  },
  incrementButtons: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(68, 129, 180, 0.05)',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  uomText: {
    color: '#BFC0C2',
    fontSize: 14,
    textAlign: 'center',
    minWidth: 35,
  },
  inputBox: {
    width: wp(3.5),
    height: hp(3.5),
    display: 'flex',
    padding: 3,
    textAlign: 'center',
    alignSelf: 'center',
  },
});
